import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../auth/AuthContext";
import { changeRequiredPassword } from "../api/authApi";

const RequiredPasswordChangePage = () => {
  const navigate = useNavigate();
  const { user, completeRequiredPasswordChange } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setPasswordError("");
    if (newPassword.length < 12) {
      setPasswordError("The new password must contain at least 12 characters.");
      return;
    }
    if (newPassword !== confirmation) {
      setError("The new passwords do not match.");
      return;
    }

    setIsSaving(true);
    try {
      await changeRequiredPassword(newPassword);
      completeRequiredPasswordChange();
      navigate("/", { replace: true });
    } catch (requestError) {
      setError(
        requestError?.detail ||
          "Unable to set your new password. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-screen-sm px-4 py-12 sm:px-6">
      <section className="rounded-xl border border-amber-200 bg-white p-6 shadow-sm sm:p-8">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-amber-700">
          Action required
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
          Set a new password
        </h1>
        <p className="mt-2 text-sm text-zinc-600">
          An administrator requires a new password for {user?.email}. Set it now
          to continue to the portal.
        </p>
        <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
          <div>
            <label
              htmlFor="required-new-password"
              className="mb-1 block text-sm font-medium text-zinc-700"
            >
              New password
            </label>
            <PasswordInput
              id="required-new-password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              disabled={isSaving}
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? "required-new-password-error" : undefined}
              className={`w-full rounded-md border px-3 py-2.5 text-sm shadow-sm outline-none transition focus:ring-2 disabled:bg-zinc-100 ${passwordError ? "border-red-600 focus:border-red-600 focus:ring-red-100" : "border-zinc-300 focus:border-blue-600 focus:ring-blue-100"}`}
            />
            {passwordError && (
              <p id="required-new-password-error" className="mt-1 text-sm text-red-700" role="alert">
                {passwordError}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="required-password-confirmation"
              className="mb-1 block text-sm font-medium text-zinc-700"
            >
              Confirm new password
            </label>
            <PasswordInput
              id="required-password-confirmation"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={isSaving}
              className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm shadow-sm outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
            />
          </div>
          {error && (
            <p className="text-sm text-red-800" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={isSaving}
            className="w-full rounded-md bg-blue-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50"
          >
            {isSaving ? "Saving password…" : "Set password and continue"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default RequiredPasswordChangePage;
